import {
  ArrowLeft,
  Check,
  CircleCheck,
  FileSpreadsheet,
  FileText,
  Folder,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  MoreHorizontal,
  Presentation,
  Radio,
  UserPlus,
  Video
} from "lucide-react";
import { MessagePane } from "./MessagePane.tsx";
import { AgentMark, Identicon } from "./AgentMark.tsx";
import type {
  DemoTarget,
  ReplicaFile,
  ReplicaPerson,
  ReplicaState,
  SpaceTab
} from "@/product-stage/demo/types.ts";

export function CenterReplica({
  state,
  elapsed,
  onSelect
}: {
  state: ReplicaState;
  elapsed: number;
  onSelect(target: DemoTarget): void;
}) {
  if (state.view === "direct") {
    const agent = state.agents.find((item) => item.id === state.active.agentId);
    const person = state.people.find((item) => item.id === state.active.personId);
    return (
      <section className="space-shell" data-mode="direct">
        <header className="direct-chat-header">
          {agent ? <AgentMark agent={agent} variant="hud" animated={agent.status === "working"} /> : null}
          {person ? <Identicon seed={person.seed} className="direct-header-avatar" /> : null}
          <div className="space-identity">
            <div>
              <strong>{state.space.name}</strong>
            </div>
            <small>{state.space.threadMeta}</small>
          </div>
          <button type="button" className="direct-members" tabIndex={-1}>
            <UserPlus />
            成员
          </button>
        </header>
        <div className="space-main">
          <MessagePane state={state} elapsed={elapsed} onSelect={onSelect} />
        </div>
      </section>
    );
  }

  if (state.view === "threads") {
    return (
      <section className="space-shell" data-mode="threads">
        <SpaceHeader state={state} onSelect={onSelect} />
        <ThreadIndex state={state} onSelect={onSelect} />
      </section>
    );
  }

  if (state.view === "messages") {
    return (
      <section className="space-shell" data-mode="messages">
        <SpaceHeader state={state} onSelect={onSelect} />
        <SpaceMessages />
      </section>
    );
  }

  if (state.view === "meeting") {
    return (
      <section className="space-shell" data-mode="meeting">
        <SpaceHeader state={state} onSelect={onSelect} />
        <MeetingHistory state={state} onSelect={onSelect} />
      </section>
    );
  }

  if (state.view === "files") {
    return (
      <section className="space-shell" data-mode="files">
        <SpaceHeader state={state} onSelect={onSelect} />
        <FilesHome state={state} />
      </section>
    );
  }

  return (
    <section className="space-shell">
      <SpaceHeader state={state} onSelect={onSelect} />
      <div className="thread-conversation-surface">
        <header>
          <button
            type="button"
            className="thread-conversation-back"
            onClick={() => onSelect({ kind: "tab", tab: "threads" })}
          >
            <ArrowLeft />
            线程列表
          </button>
          <div className="thread-conversation-room">
            <MessagesSquare aria-hidden />
            <span>
              <strong>{state.space.threadTitle}</strong>
              <small>{state.space.name}</small>
            </span>
          </div>
          <button type="button" className="thread-enter" tabIndex={-1}>
            <MessageSquare />
            进入空间
          </button>
        </header>
        <div className="thread-conversation-body">
          <MessagePane state={state} elapsed={elapsed} onSelect={onSelect} />
        </div>
      </div>
    </section>
  );
}

function SpaceHeader({
  state,
  onSelect
}: {
  state: ReplicaState;
  onSelect(target: DemoTarget): void;
}) {
  const tabs: Array<{ tab: SpaceTab; label: string; icon: typeof MessageCircle }> = [
    { tab: "messages", label: "对话", icon: MessageCircle },
    { tab: "threads", label: "线程", icon: MessagesSquare },
    { tab: "documents", label: "文件", icon: Folder }
  ];
  return (
    <header className="space-header">
      <div className="space-heading-row">
        <div className="space-identity">
          <div>
            <strong>
              <span>#</span>
              {state.space.name}
            </strong>
            <em>{state.space.badge}</em>
          </div>
          <small>{state.space.members}</small>
        </div>
        <div className="space-heading-tools">
          <span className="space-presence" aria-label="林深、周妍、陈航在场">
            <Identicon seed={state.account.name} className="avatar" />
            {state.people.map((person) => (
              <Identicon key={person.id} seed={person.seed} className="avatar" />
            ))}
          </span>
          <button type="button" className="icon-round" aria-label="添加成员" tabIndex={-1}>
            <UserPlus />
          </button>
          <button type="button" className="icon-round" aria-label="更多" tabIndex={-1}>
            <MoreHorizontal />
          </button>
        </div>
      </div>
      <div className="space-tabs">
        {tabs.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.tab}
              type="button"
              data-active={state.space.tab === item.tab}
              onClick={() => onSelect({ kind: "tab", tab: item.tab })}
            >
              <Icon />
              {item.label}
            </button>
          );
        })}
      </div>
    </header>
  );
}

function MeetingHistory({
  state,
  onSelect
}: {
  state: ReplicaState;
  onSelect(target: DemoTarget): void;
}) {
  const meeting = state.meeting;
  if (!meeting) return null;
  return (
    <div className="meeting-history">
      <header className="meeting-history-header">
        <span className="meeting-title-mark" data-live={meeting.status === "live"}>
          <Video />
        </span>
        <div>
          <strong>{meeting.title}</strong>
          <small>关联到 {state.space.name} · 会议历史持续同步</small>
        </div>
        <span className="meeting-live-state" data-status={meeting.status}>
          {meeting.status === "live" ? <Radio /> : <CircleCheck />}
          {meeting.statusLabel}
        </span>
      </header>

      <div className="meeting-metrics">
        <span>
          <strong>{meeting.duration}</strong>
          <small>已同步</small>
        </span>
        <span>
          <strong>{meeting.syncedSegments}</strong>
          <small>转写片段</small>
        </span>
        <span>
          <strong>{meeting.candidateDecisions}</strong>
          <small>候选决定</small>
        </span>
        <span>
          <strong>{meeting.actions}</strong>
          <small>待办</small>
        </span>
      </div>

      <div className="meeting-history-body">
        <div className="meeting-transcript">
          <div className="meeting-section-label">
            <strong>实时会议历史</strong>
            <small>说话人与时间点由会议系统提供</small>
          </div>
          {meeting.lines.length === 0 ? (
            <div className="meeting-waiting">
              <Radio />
              正在等待第一段发言
            </div>
          ) : (
            meeting.lines.map((line) => {
              const person = meetingPerson(state, line.personId);
              return (
                <article key={line.id} className="meeting-line">
                  <Identicon seed={person.seed} className="meeting-line-avatar" />
                  <div>
                    <span>
                      <strong>{person.name}</strong>
                      <em>{person.role}</em>
                      <time>{line.timestamp}</time>
                    </span>
                    <p>{line.text}</p>
                  </div>
                </article>
              );
            })
          )}
        </div>

        {meeting.summary ? (
          <aside className="meeting-summary">
            <div className="meeting-section-label">
              <strong>会后总结</strong>
              <small>每项决定保留原始时间点</small>
            </div>
            <div className="meeting-summary-stats">
              {meeting.summary.stats.map((stat) => (
                <span key={stat.label}>
                  <strong>{stat.value}</strong>
                  <small>{stat.label}</small>
                </span>
              ))}
            </div>
            <ol>
              {meeting.summary.decisions.map((decision) => (
                <li key={decision}>
                  <Check />
                  <span>{decision}</span>
                </li>
              ))}
            </ol>
            <button
              type="button"
              className="meeting-confirm"
              data-confirmed={meeting.confirmed}
              onClick={() => onSelect({ kind: "action", action: "confirm-meeting" })}
            >
              {meeting.confirmed ? <CircleCheck /> : <Check />}
              {meeting.confirmed ? "3 项决定已确认并发布" : "确认 3 项决定"}
            </button>
          </aside>
        ) : null}
      </div>
    </div>
  );
}

function ThreadSenderMark({ state, sender }: { state: ReplicaState; sender: string }) {
  const agent = state.agents.find((item) => item.name === sender);
  if (agent) {
    return <AgentMark agent={agent} variant="sidebar" />;
  }
  const person = state.people.find((item) => item.name === sender);
  const seed = person?.seed ?? (sender === state.account.name ? state.account.name : sender);
  return <Identicon seed={seed} className="avatar" />;
}

function meetingPerson(state: ReplicaState, id: string): ReplicaPerson {
  if (id === "self") {
    return { id: "self", name: state.account.name, role: "售前方案经理", seed: state.account.name };
  }
  const person = state.people.find((item) => item.id === id);
  if (!person) throw new Error(`Unknown meeting person ${id}`);
  return person;
}

function ThreadIndex({
  state,
  onSelect
}: {
  state: ReplicaState;
  onSelect(target: DemoTarget): void;
}) {
  return (
    <div className="thread-index-surface">
      <header>
        <div>
          <strong>线程</strong>
          <small>{state.space.name}</small>
        </div>
      </header>
      <div className="thread-index-content">
        <div className="indexed-thread-list">
          {state.threads.map((thread) => (
            <button
              key={thread.id}
              type="button"
              className="indexed-thread-item"
              onClick={() => onSelect({ kind: "scene", sceneId: thread.sceneId })}
            >
              <ThreadSenderMark state={state} sender={thread.sender} />
              <span>
                <span className="indexed-thread-meta">
                  <strong>{thread.sender}</strong>
                  <time>{thread.time}</time>
                </span>
                <b>{thread.title}</b>
                <small>{thread.preview}</small>
              </span>
              <i>{thread.replies}</i>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SpaceMessages() {
  return (
    <div className="space-messages">
      <div className="space-empty">
        <MessageSquare />
        <strong>在这个空间里开始工作</strong>
        <small>任务和结论在线程里推进。点上方「线程」查看回收期口径与会前材料。</small>
      </div>
    </div>
  );
}

function FilesHome({ state }: { state: ReplicaState }) {
  return (
    <div className="files-layout">
      <aside className="folder-sidebar">
        <div className="section-title">
          <span>{state.space.name}</span>
          <small>当前项目文件</small>
        </div>
        <div className="folder-tree">
          <div className="tree-row" data-active="true">
            <button type="button" tabIndex={-1}>
              <Folder />
              客户澄清材料
            </button>
          </div>
        </div>
      </aside>
      <div className="file-browser">
        <div className="file-header">
          <strong>客户澄清材料</strong>
          <small>{state.files.length} 个文件 · 来源持续保留</small>
        </div>
        <div className="project-file-table">
          <div className="project-file-table-head">
            <span>名称</span>
            <span>来源</span>
            <span>说明</span>
          </div>
          {state.files.map((file) => (
            <button key={file.name} type="button" tabIndex={-1}>
              <span data-kind={file.kind}>{fileIcon(file)}</span>
              <strong>{file.name}</strong>
              <em>{file.provenance ?? "项目文件"}</em>
              <small>{file.detail}</small>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function fileIcon(file: ReplicaFile) {
  if (file.kind === "xlsx") return <FileSpreadsheet />;
  if (file.kind === "pptx") return <Presentation />;
  return <FileText />;
}
